/**
 * DistributionSelection
 * ---------------------
 * 
 * June 2026
 */

import Constant from './Constant.js';
import Erlang from './Erlang.js';
import Exponential from './Exponential.js';
import Normal from './Normal.js';
import Poisson from './Poisson.js';
import Triangular from './Triangular.js';
import Uniform from './Uniform.js';

import { Dropdown } from 'primereact/dropdown';

// Available probability distributions to select.
const distOptions = [
    'constant',
    'uniform',
    'triangular',
    'exponential',
    'poisson',
    'normal',
    'erlang'
]

/**
 * Selector component for applying probability distributions to an element's attribute.
 * 
 * @param {Object} element          - Attributes for an element being edited.
 * @param {Object} attr             - Distribution parameters tied with element attribute.
 * @param {String} dist             - Probability distribution currently selected.
 * @param {String} attrLabel        - Element attribute tied with the distribution selector.
 * @param {Function} distUpdater    - Sets which distribution is currently being used.
 */
function DistributionSelection({ elementType, element, attr, dist, attrLabel, distUpdater, warningUpdater, configWarnings }) {

    /**
     * Updates an attribute's chosen distribution.
     * 
     * @param {String} dist - New distribution being set.
     */
    const handleDistribution = (dist) => {
        warningUpdater(element.gid, attr, null);

        let newDistribution = {};
        switch (dist) {
            case 'constant':
                newDistribution = {'dist': 'constant', 'attr': {'value': 1}};
                break;
            case 'uniform':
                newDistribution = {'dist': 'uniform', 'attr': {'lower_bound': 1, 'upper_bound': 2}};
                break;
            case 'triangular':
                newDistribution = {'dist': 'triangular', 'attr': {'lower_bound': 1, 'upper_bound': 3, 'mode': 2}};
                break;
            case 'exponential':
                newDistribution = {'dist': 'exponential', 'attr': {'lambda': 1}};
                break;
            case 'poisson':
                newDistribution = {'dist': 'poisson', 'attr': {'lambda': 1}};
                break;
            case 'normal':
                newDistribution = {'dist': 'normal', 'attr': {'mean': 0, 'std_deviation': 1}};
                break;
            case 'erlang':
                newDistribution = {'dist': 'erlang', 'attr': {'shape': 1, 'scale': 1}};
                break;
        }

        distUpdater(attr, newDistribution);
    }

    /**
     * Renders the probability distribution component based on the distribution selected.
     * 
     * @returns {JSX.Element} Distribution component.
     */
    const renderDistribution = () => {
        switch (dist) {
            case 'constant':
                return <Constant elementType={elementType} element={element} attr={attr} distUpdater={distUpdater} warningUpdater={warningUpdater} configWarnings={configWarnings}/>;
            case 'uniform':
                return <Uniform elementType={elementType} element={element} attr={attr} distUpdater={distUpdater} warningUpdater={warningUpdater} configWarnings={configWarnings}/>;
            case 'triangular':
                return <Triangular elementType={elementType} element={element} attr={attr} distUpdater={distUpdater} warningUpdater={warningUpdater} configWarnings={configWarnings}/>;
            case 'exponential':
                return <Exponential elementType={elementType} element={element} attr={attr} distUpdater={distUpdater} warningUpdater={warningUpdater} configWarnings={configWarnings}/>;
            case 'poisson':
                return <Poisson elementType={elementType} element={element} attr={attr} distUpdater={distUpdater} warningUpdater={warningUpdater} configWarnings={configWarnings}/>;
            case 'normal':
                return <Normal elementType={elementType} element={element} attr={attr} distUpdater={distUpdater} warningUpdater={warningUpdater} configWarnings={configWarnings}/>;
            case 'erlang':
                return <Erlang elementType={elementType} element={element} attr={attr} distUpdater={distUpdater} warningUpdater={warningUpdater} configWarnings={configWarnings}/>;
        }
    }

    return (
        <div>
            <div style={{ marginBottom: '0.5rem'}}>
                <label style={{ marginRight: '0.5rem'}}>{attrLabel}: </label>
                <Dropdown value={dist} options={distOptions} onChange={(event) => handleDistribution(event.target.value)}/>
            </div>
            {renderDistribution()}
         </div>
    )
}

export default DistributionSelection;